import { useMemo } from 'react';
import { MovementOverlay, MovementVector, MoveGhost, HordeAreas, PingOverlay } from '../BattlefieldOverlays';
import BattlefieldMeasureLine from '../BattlefieldMeasureLine';
import BattlefieldFiringLine from '../BattlefieldFiringLine';
import BattlefieldDeployOverlay, { TeamArrivalFlags } from '../BattlefieldDeployOverlay';
import { liveryColour, liveryFor, teamsIn } from '../../engine/roster/team';
import BattlefieldBuildOverlay from '../BattlefieldBuildOverlay';
import { tokenEye } from '../../engine/battlefield/tokens';
import { hordesOnMap } from './markerDecorations';
import PanelTemplates from './PanelTemplates';
import PanelMarkers from './PanelMarkers';
import { EMPTY } from './panelDefaults';
import { CoverGlassDefs } from '../display/CoverScreens';
import displayStyles from '../../styles/Display.module.css';

/**
 * The live pair, eye to eye. The firing line yields to the tape and to
 * the furniture modes, and needs both of them on the map.
 */
function FiringLine({ panel, map }) {
  const { firingLine, actorId, targetId } = panel;
  // Where the figures are DRAWN (a walker mid-route), not where they are committed.
  const tokens = map.drawnTokens;
  const quiet = !firingLine || map.line || map.building || map.deploying;
  if (quiet || !tokens[actorId] || !tokens[targetId] || actorId === targetId) return null;
  return (
    <BattlefieldFiringLine
      from={tokenEye(tokens[actorId])}
      to={tokenEye(tokens[targetId])}
      label={firingLine.label}
      blocked={!!firingLine.blocked}
      camera={map.camera}
    />
  );
}

/** The route of the marker being carried, and what it is costing; dashed while it is a ghost's. */
function RouteVector({ vector, camera, bands, ghost, noLabel }) {
  if (!vector) return null;
  const { path, budget, crossed, vertical } = vector;
  return <MovementVector {...{ path, budget, crossed, vertical, camera, bands, ghost, noLabel }} />;
}

/**
 * Deploy's ghosts. THE ACTIVE ROOM, not the roster: the ghosts answer
 * "where will they land", and the roster is everyone already standing on
 * the map - so it previewed placing people who were placed (v8.22.0).
 */
function DeployGhosts({ panel, map }) {
  const count = map.activeRoom?.entries.length;
  return (
    <>
      <BattlefieldDeployOverlay
        marker={panel.deployment}
        camera={map.camera}
        ghostCount={count ?? 0}
        tileSize={map.tile}
        line={map.formationLine && { ...map.formationLine, total: count }}
      />
      <TeamArrivalFlags points={teamFlagsOf(panel.arrivals, panel.roster)} camera={map.camera} />
    </>
  );
}

/** Each team point with its team's name and livery colour; a tag nobody on the roster carries reads as itself. */
function teamFlagsOf(arrivals, roster = []) {
  const entries = Object.entries(arrivals ?? {});
  if (!entries.length) return [];
  const teams = teamsIn(roster);
  return entries.map(([tag, point]) => {
    const team = teams.find((t) => t.tag === tag);
    const colour = liveryColour(team ? liveryFor(team, roster) : null);
    return { tag, label: team?.label || tag, colour, point };
  });
}

/** What Build is laying down, before it is laid. */
function BuildPreview({ map }) {
  const { build } = map;
  const selecting = build.tool === 'select';
  return (
    <BattlefieldBuildOverlay
      camera={map.camera}
      size={map.tile}
      arenaDraft={build.arenaDraft}
      hover={build.hover}
      preview={build.preview}
      region={selecting ? build.region : null}
      ghost={selecting ? build.ghost : null}
    />
  );
}

/**
 * Everything drawn inside the scene, bottom to top: horde areas (ground,
 * which must never swallow a pointer aimed at a marker), the reach and
 * route, the templates, the markers, the tape, the firing line, the pings
 * - over the map, under nothing: a ping is the most urgent thing on screen
 * for the six seconds it lasts - and Deploy's or Build's own preview.
 */
/**
 * The take-cover cue's tooltip (2026-10-25): the screen under the pointer, found by geometry (screens never take
 * the pointer), as a small box beside it. No text is ever drawn on the screens themselves.
 */
function CoverScreenTip({ tip }) {
  if (!tip) return null;
  const width = 14 + tip.text.length * 6.6;
  return (
    <g className={displayStyles.coverTip} data-testid="cover-tip" transform={`translate(${tip.x + 14},${tip.y - 30})`}>
      <rect className={displayStyles.coverTipBox} width={width} height={22} rx={5} />
      <text className={displayStyles.coverTipText} x={7} y={15}>
        {tip.text}
      </text>
    </g>
  );
}

export default function PanelSceneLayers({ panel, map }) {
  const { roster } = panel;
  // Where the figures are DRAWN (a walker mid-route), not where they are committed.
  const { camera, movementBands, drawnTokens: tokens } = map;
  const hordes = useMemo(() => hordesOnMap(roster, tokens), [roster, tokens]);
  return (
    <>
      <CoverGlassDefs />
      <HordeAreas hordes={hordes} camera={camera} />
      {/* How loud the bands are is this viewer's (movementBands, 2026-10-21): the drawing only. */}
      <MovementOverlay
        tiles={map.reach}
        tileSize={map.boundary.tileSize}
        camera={camera}
        bands={movementBands}
        look={map.prefs.movementBands}
        graded={map.cover?.graded}
        gradeMax={map.cover?.gradeMax}
        ripple={map.cover?.ripple}
      />
      <RouteVector
        vector={map.vector}
        camera={camera}
        bands={movementBands}
        ghost={!!map.carried}
        noLabel={!!map.cover?.graded}
      />
      {/* The ghost (plan design-gaps, M2): where the carried marker would stand, under the figures. */}
      {map.carried && (
        <MoveGhost
          carried={map.carried}
          camera={camera}
          screens={map.cover?.ghost?.screens}
          riseKey={map.cover?.ghostRiseKey}
          still={map.cover?.still}
        />
      )}
      <PanelTemplates panel={panel} map={map} />
      <PanelMarkers panel={panel} map={map} />
      <BattlefieldMeasureLine line={map.line} camera={camera} />
      <FiringLine panel={panel} map={map} />
      <PingOverlay pings={map.prefs.showPings ? panel.pings : EMPTY} camera={camera} tokens={tokens} />
      <CoverScreenTip tip={map.cover?.tip} />
      {map.deploying && <DeployGhosts panel={panel} map={map} />}
      {map.building && <BuildPreview map={map} />}
    </>
  );
}
